import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import {
    addMachineFavorite,
    deleteMachineFavorite,
} from '../../services/api/mriMachineFavouriteService';
import { useToast } from '../../hooks/use-toast';

interface FavoriteButtonProps {
    machineId: string;
    machineName: string;
    isFavorited: boolean;
    onFavoriteChange?: (machineId: string, isFavorited: boolean) => void;
}

export default function FavoriteButton({
    machineId,
    machineName,
    isFavorited,
    onFavoriteChange,
}: FavoriteButtonProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [favorited, setFavorited] = useState(isFavorited);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        setFavorited(isFavorited);
    }, [isFavorited]);

    const handleClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isUpdating) return;

        setIsUpdating(true);
        try {
            if (favorited) {
                await deleteMachineFavorite(machineId);
                toast({
                    title: t('roomOverview.machineCard.favorites.removed'),
                    description: t(
                        'roomOverview.machineCard.favorites.removedDescription',
                        { name: machineName }
                    ),
                });
            } else {
                await addMachineFavorite(machineId);
                toast({
                    title: t('roomOverview.machineCard.favorites.added'),
                    description: t(
                        'roomOverview.machineCard.favorites.addedDescription',
                        { name: machineName }
                    ),
                });
            }
            setFavorited(!favorited);
            onFavoriteChange?.(machineId, !favorited);
        } catch (error) {
            console.error('Failed to update favorite:', error);
            toast({
                title: t('roomOverview.machineCard.favorites.error'),
                description: t(
                    'roomOverview.machineCard.favorites.errorDescription'
                ),
                variant: 'destructive',
            });
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={handleClick}
            disabled={isUpdating}
            aria-pressed={favorited}
            aria-label={t(
                favorited
                    ? 'roomOverview.machineCard.favorites.removeAria'
                    : 'roomOverview.machineCard.favorites.addAria',
                { name: machineName }
            )}
        >
            <Star
                className={cn(
                    'h-4 w-4',
                    favorited
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-subtle-foreground'
                )}
            />
        </Button>
    );
}
